import { useMutation, useQuery } from "@apollo/client";
import ReportProblem from "@mui/icons-material/ReportProblem";
import SignalCellularAlt from "@mui/icons-material/SignalCellularAlt";
import SignalCellularAlt1Bar from "@mui/icons-material/SignalCellularAlt1Bar";
import SignalCellularAlt2Bar from "@mui/icons-material/SignalCellularAlt2Bar";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { LinearIcon } from "components/LinearIcon";
import {
  ERROR_CLUSTER_RUN_ID_KEY,
  ERROR_CLUSTER_GROUP_ID_KEY,
} from "encamp-omnisearch/src/constants/keyNames";
import { gql } from "generated-graphql";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useState } from "react";
import { UPDATE_WORKFLOW_TAGS_BULK_MUTATION } from "routes/Staff/Fulfillment/WorkflowActivitiesTable/ManageWorkflowTags";

const ERROR_CLUSTER_FOR_WORKFLOW_QUERY = gql(`
  query ErrorClusterForWorkflow($workflowId: ID!) {
    errorClusterForWorkflow(workflowId: $workflowId) {
      runId
      groupId
      groupSize
      errorPattern
      states
      workflowsWithTags {
        workflowId
        tags
      }
    }
  }
`);

const CREATE_LINEAR_ISSUE_MUTATION = gql(`
  mutation CreateLinearIssueFromFulfillment($input: CreateLinearIssueInput!) {
    createLinearIssue(input: $input) {
      success
      issueIdentifier
      issueUrl
      issueId
    }
  }
`);

interface FacilityInfo {
  id: string;
  name: string;
  city: string;
  state: string;
  streetAddress1?: string | null;
}

interface TenantInfo {
  id: string;
  name: string;
}

interface ActivityInfo {
  id: string;
  type: string;
  description?: string | null;
}

interface CreateLinearIssueDialogProps {
  open: boolean;
  onClose: () => void;
  workflowId: string;
  reportId: string;
  facility: FacilityInfo;
  tenant: TenantInfo;
  activity: ActivityInfo;
  errorMessage: string;
}

export function CreateLinearIssueDialog({
  open,
  onClose,
  workflowId,
  reportId,
  facility,
  tenant,
  activity,
  errorMessage,
}: CreateLinearIssueDialogProps) {
  const alerts = useAlerts();
  const [isCreating, setIsCreating] = useState(false);
  const [createForCluster, setCreateForCluster] = useState(false);
  const [linearTicketTitle, setLinearTicketTitle] = useState("");
  const [linearTicketStatus, setLinearTicketStatus] = useState("Triage");
  const [linearTicketPriority, setLinearTicketPriority] = useState(0);

  // Query for error cluster information
  const { data: clusterData, loading: clusterLoading } = useQuery(
    ERROR_CLUSTER_FOR_WORKFLOW_QUERY,
    {
      variables: { workflowId },
      skip: !open || !workflowId,
    }
  );

  const errorCluster = clusterData?.errorClusterForWorkflow;
  const isPartOfCluster = errorCluster && errorCluster.groupSize > 1;

  // Mutations
  const [createLinearIssue] = useMutation(CREATE_LINEAR_ISSUE_MUTATION);
  const [updateWorkflowTags] = useMutation(UPDATE_WORKFLOW_TAGS_BULK_MUTATION);

  // Build the fulfillment details URL
  const fulfillmentDetailsUrl = useMemo(() => {
    return `${window.location.origin}/staff/fulfillment/r/${reportId}/activities/${activity.id}`;
  }, [reportId, activity.id]);

  // Build the workflows list URL for cluster view
  const viewWorkflowsUrl = useMemo(() => {
    if (!errorCluster) return null;
    const omnisearch = encodeURIComponent(
      `${ERROR_CLUSTER_RUN_ID_KEY}:${errorCluster.runId} ${ERROR_CLUSTER_GROUP_ID_KEY}:${errorCluster.groupId}`
    );
    return `${window.location.origin}/staff/fulfillment/workflows?omnisearch=${omnisearch}`;
  }, [errorCluster]);

  // Build ticket info based on whether creating for single or cluster
  const buildTicketInfo = useCallback(() => {
    if (createForCluster && errorCluster) {
      // Cluster mode - similar to ErrorClustering page
      const sortedStates = [...errorCluster.states].sort();
      const statesDisplay =
        sortedStates.length <= 3 ? sortedStates.join(", ") : "Multiple States";
      const truncatedPattern =
        errorCluster.errorPattern.length > 25
          ? `${errorCluster.errorPattern.slice(0, 25)}...`
          : errorCluster.errorPattern;
      const title = `[${errorCluster.groupSize}: ${statesDisplay}] Fulfillment Error: ${truncatedPattern}`;
      const description = [
        `**Error Pattern**: ${errorCluster.errorPattern}`,
        "",
        `**View Workflows**: ${viewWorkflowsUrl}`,
        "",
        `**Example Fulfillment Detail**: ${fulfillmentDetailsUrl}`,
        "",
        `**Group Size**: ${errorCluster.groupSize}`,
        "",
        `**States**: ${sortedStates.join(", ")}`,
        "",
        `**Example Facility**: ${facility.name}`,
        `**Customer**: ${tenant.name}`,
      ].join("\n");

      return { title, description };
    } else {
      // Single workflow mode
      const truncatedError =
        errorMessage.length > 50
          ? `${errorMessage.slice(0, 50)}...`
          : errorMessage;
      const title = `[${facility.state}] Fulfillment Error: ${facility.name} - ${truncatedError}`;
      const description = [
        `**Error Message**: ${errorMessage}`,
        "",
        `**Fulfillment Details**: ${fulfillmentDetailsUrl}`,
        "",
        `**Facility**: ${facility.name}`,
        `**Address**: ${facility.streetAddress1 || ""}, ${facility.city}, ${
          facility.state
        }`,
        `**Customer**: ${tenant.name}`,
        "",
        `**Workflow ID**: ${workflowId}`,
        `**Activity**: ${activity.description || activity.type}`,
      ].join("\n");

      return { title, description };
    }
  }, [
    createForCluster,
    errorCluster,
    errorMessage,
    facility,
    tenant,
    workflowId,
    activity,
    fulfillmentDetailsUrl,
    viewWorkflowsUrl,
  ]);

  // Keep editable title in sync with computed default when dialog opens or cluster toggle changes
  const ticketInfo = useMemo(() => buildTicketInfo(), [buildTicketInfo]);

  useEffect(() => {
    if (!open) return;
    setLinearTicketTitle(ticketInfo.title);
    setLinearTicketStatus("Triage");
    setLinearTicketPriority(0);
  }, [open, ticketInfo.title]);

  const handleConfirm = useCallback(async () => {
    setIsCreating(true);

    try {
      const { description } = buildTicketInfo();

      // Create the Linear issue
      const result = await createLinearIssue({
        variables: {
          input: {
            title: linearTicketTitle,
            description,
            status: linearTicketStatus,
            priority: linearTicketPriority,
          },
        },
      });

      const issueIdentifier = result.data?.createLinearIssue?.issueIdentifier;
      const issueUrl = result.data?.createLinearIssue?.issueUrl;

      if (!issueIdentifier) {
        throw new Error("Failed to get issue identifier from Linear");
      }

      alerts.success(`Linear ticket ${issueIdentifier} created successfully!`);

      // Determine which workflows to tag
      let workflowUpdates: Array<{
        workflowId: string;
        tagNames: string[];
      }> = [];

      if (createForCluster && errorCluster) {
        // Tag all workflows in the cluster
        workflowUpdates = errorCluster.workflowsWithTags.map((workflow) => ({
          workflowId: workflow.workflowId,
          tagNames: [
            ...new Set([...workflow.tags, issueIdentifier, "engineering"]),
          ],
        }));
      } else {
        // Tag only the current workflow
        const currentWorkflowTags =
          errorCluster?.workflowsWithTags.find(
            (w) => w.workflowId === workflowId
          )?.tags ?? [];
        workflowUpdates = [
          {
            workflowId,
            tagNames: [
              ...new Set([
                ...currentWorkflowTags,
                issueIdentifier,
                "engineering",
              ]),
            ],
          },
        ];
      }

      if (workflowUpdates.length > 0) {
        await updateWorkflowTags({
          variables: {
            workflowUpdates,
          },
        });

        alerts.success(
          `Tagged ${workflowUpdates.length} ${pluralize(
            "workflow",
            workflowUpdates.length
          )} with ${issueIdentifier}`
        );
      }

      // Open the issue in a new tab
      if (issueUrl) {
        window.open(issueUrl, "_blank", "noopener");
      }

      onClose();
    } catch (err) {
      alerts.error("Failed to create Linear ticket", err);
    } finally {
      setIsCreating(false);
    }
  }, [
    buildTicketInfo,
    createLinearIssue,
    updateWorkflowTags,
    createForCluster,
    errorCluster,
    workflowId,
    alerts,
    onClose,
    linearTicketTitle,
    linearTicketStatus,
    linearTicketPriority,
  ]);

  const handleClose = useCallback(() => {
    setCreateForCluster(false);
    onClose();
  }, [onClose]);

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      header="Create Linear Ticket"
      confirmText="Create Linear Ticket"
      cancelText="Cancel"
      loading={isCreating}
      confirmStartIcon={<LinearIcon />}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          A Linear ticket will be created in the Engineering Support project
          with the following information:
        </Typography>

        {/* Cluster notification and toggle */}
        {!clusterLoading && isPartOfCluster && (
          <Alert severity="info" sx={{ alignItems: "flex-start" }}>
            <Stack spacing={1}>
              <Typography variant="body2">
                This error is part of an Error Cluster group with{" "}
                <strong>{errorCluster.groupSize}</strong>{" "}
                {pluralize("report", errorCluster.groupSize)}.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={createForCluster}
                    onChange={(e) => setCreateForCluster(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Create ticket for entire cluster group (
                    {errorCluster.groupSize}{" "}
                    {pluralize("report", errorCluster.groupSize)})
                  </Typography>
                }
              />
            </Stack>
          </Alert>
        )}

        <TextField
          label="Title"
          value={linearTicketTitle}
          onChange={(e) => setLinearTicketTitle(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "start",
            mb: 2,
          }}
        >
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={linearTicketPriority}
              onChange={(e) => setLinearTicketPriority(e.target.value as number)}
              label="Priority"
            >
              <MenuItem value={0}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        width: 24,
                        textAlign: "center",
                      }}
                    >
                      ---
                    </Typography>
                    <Typography>No priority</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    0
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={1}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ReportProblem fontSize="small" />
                    <Typography>Urgent</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    1
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SignalCellularAlt fontSize="small" />
                    <Typography>High</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    2
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={3}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ position: "relative", width: 24, height: 24 }}>
                      <SignalCellularAlt
                        fontSize="small"
                        sx={{ position: "absolute", inset: 0, opacity: 0.18 }}
                      />
                      <SignalCellularAlt2Bar
                        fontSize="small"
                        sx={{ position: "absolute", inset: 0 }}
                      />
                    </Box>
                    <Typography>Medium</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    3
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={4}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ position: "relative", width: 24, height: 24 }}>
                      <SignalCellularAlt
                        fontSize="small"
                        sx={{ position: "absolute", inset: 0, opacity: 0.18 }}
                      />
                      <SignalCellularAlt1Bar
                        fontSize="small"
                        sx={{ position: "absolute", inset: 0 }}
                      />
                    </Box>
                    <Typography>Low</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    4
                  </Typography>
                </Box>
              </MenuItem>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <RadioGroup
                  value={linearTicketStatus}
                  onChange={(e) => setLinearTicketStatus(e.target.value)}
                  row
                >
                  <FormControlLabel
                    value="Triage"
                    control={<Radio />}
                    label="Triage"
                  />
                  <FormControlLabel
                    value="Todo"
                    control={<Radio />}
                    label="Todo"
                  />
                </RadioGroup>
              </FormControl>
            </Box>

        <Box
          sx={{
            bgcolor: "action.hover",
            borderRadius: 1,
            p: 2,
          }}
        >

          <Typography variant="subtitle2" gutterBottom>
            {createForCluster ? "Error Pattern" : "Error Message"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, wordBreak: "break-word" }}>
            {createForCluster && errorCluster
              ? errorCluster.errorPattern
              : errorMessage}
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Facility
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {facility.name} ({facility.city}, {facility.state})
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Customer
          </Typography>
          <Typography variant="body2">{tenant.name}</Typography>

          {createForCluster && errorCluster && (
            <>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Group Details
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <Typography component="li" variant="body2">
                  Group Size: {errorCluster.groupSize}
                </Typography>
                <Typography component="li" variant="body2">
                  States: {[...errorCluster.states].sort().join(", ")}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary">
          {createForCluster && errorCluster ? (
            <>
              After creation, all {errorCluster.workflowsWithTags.length}{" "}
              {pluralize("workflow", errorCluster.workflowsWithTags.length)} in
              this group will be tagged with the ticket ID and "engineering".
            </>
          ) : (
            <>
              After creation, this workflow will be tagged with the ticket ID
              and "engineering".
            </>
          )}
        </Typography>
      </Stack>
    </ConfirmDialog>
  );
}
